import {
  KeyboardArrowDown,
  KeyboardArrowUp,
  Refresh,
} from "@mui/icons-material"
import {
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import { FC, useState } from "react"
import { useTranslation } from "react-i18next"
import { MEvent } from "shared/mevent"
import { SharedAccessPatternSaEnum } from "shared/sharedAccessPatternsData"
import { SupportedThingTypeMrns } from "shared/thingTypeData"
import { useGetThingsQuery } from "src/features/things/thingsApi"
import { useSearchParamsState } from "src/hooks/useSearchParamsState"
import { useAppSelector } from "../app/hooks"
import { selectMeIsReady } from "../features/me/meSlice"
import { MeventsList } from "./MeventsList"
import SaLayout from "./SaLayout"

//  build list of object {value, label} with label replace : by _
const ThingTypeMrns = SupportedThingTypeMrns.map(
  (tt) =>
    ({
      value: tt,
      //replace all : by _
      label: tt.replace(/:/g, "_"),
    } as { value: string; label: string }),
)

const cardSx = {
  bgcolor: "white",
  border: "1px solid",
  borderColor: "rgba(0, 0, 0, 0.08)",
  borderRadius: 2,
  boxShadow: "0 1px 2px rgba(35, 66, 133, 0.04)",
}

export const SaMeventsPage: FC = () => {
  const { t } = useTranslation()

  const isReady = useAppSelector(selectMeIsReady)

  const [traceId, setTraceId] = useSearchParamsState("traceId", "")

  const [localTraceId, setLocalTraceId] = useState(traceId)

  const [thingType, setThingType] = useSearchParamsState("thingType", "")

  const qLog = useGetThingsQuery(
    {
      params: {
        "ap.name": SharedAccessPatternSaEnum.SaGetMeventLogByTrace,
        "ap.traceId": traceId,
        "ap.limit": 400,
      },
    },
    { skip: !isReady || traceId === "" },
  )

  const mevents: MEvent[] = qLog.data?.things || []

  const qThingType = useGetThingsQuery(
    {
      params: {
        "ap.name": SharedAccessPatternSaEnum.SaGetMeventLogByThingType,
        "ap.thingType": thingType,
        "ap.limit": 400,
      },
    },
    { skip: !isReady || !thingType },
  )

  const thingTypeEvents: MEvent[] = qThingType.data?.things || []

  const [thingTypeFilterExpanded, setThingTypeFilterExpanded] = useState(true)

  const isLogLoading = qLog.isLoading || qLog.isFetching
  const isThingTypeLoading = qThingType.isLoading || qThingType.isFetching

  return (
    <SaLayout selectedMenuItemLabelKey="meventLog">
      <Box
        sx={{
          maxWidth: 1400,
          mx: "auto",
          px: { xs: 3, md: 5 },
          py: { xs: 4, md: 5 },
          width: "100%",
        }}
      >
        {/* Header */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-end"
          flexWrap="wrap"
          gap={2}
          sx={{ mb: 4 }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: "0.72rem",
                fontWeight: 600,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: "primary.main",
              }}
            >
              {t("adminApp")}
            </Typography>
            <Stack direction="row" spacing={1.5} alignItems="baseline">
              <Typography
                variant="h4"
                sx={{
                  mt: 0.5,
                  fontWeight: 700,
                  letterSpacing: "-0.02em",
                  color: "text.primary",
                  lineHeight: 1.15,
                }}
              >
                {t("meventLog")}
              </Typography>
            </Stack>
            <Typography
              variant="body2"
              sx={{ mt: 1, color: "text.secondary" }}
            >
              {t("meventLogDescription")}
            </Typography>
          </Box>
        </Stack>

        {/* Two-column content */}
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2.5}
          alignItems="flex-start"
        >
          {/* Trace ID search column */}
          <Stack
            direction="column"
            spacing={2}
            sx={{ width: { xs: "100%", md: 500 }, flexShrink: 0 }}
          >
            <Box sx={{ ...cardSx, p: { xs: 2, md: 2.5 } }}>
              <Stack direction="column" spacing={1.5}>
                <Typography variant="body1" fontWeight={"bold"}>
                  {t("searchByTraceId")}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t("searchByTraceIdDescription")}
                </Typography>
                <TextField
                  label={t("traceId")}
                  value={localTraceId}
                  onChange={(e) => setLocalTraceId(e.target.value)}
                  onBlur={() => {
                    setTraceId(localTraceId)
                  }}
                  size="small"
                />
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography
                    variant="body2"
                    sx={{ color: "text.secondary", fontWeight: 500 }}
                  >{`${mevents?.length} events`}</Typography>
                  {isLogLoading ? (
                    <CircularProgress size={22} />
                  ) : (
                    <IconButton
                      onClick={() => {
                        qLog.refetch()
                      }}
                      size="small"
                      sx={{
                        bgcolor: "rgba(35, 66, 133, 0.06)",
                        color: "primary.main",
                        "&:hover": {
                          bgcolor: "rgba(35, 66, 133, 0.12)",
                        },
                      }}
                    >
                      <Refresh fontSize="small" />
                    </IconButton>
                  )}
                </Stack>
              </Stack>
            </Box>
            <Box sx={{ ...cardSx, overflow: "hidden" }}>
              <MeventsList mevents={mevents} />
            </Box>
          </Stack>

          {/* Thing type search column */}
          <Stack
            direction="column"
            spacing={2}
            sx={{ flex: 1, minWidth: 0, width: "100%" }}
          >
            <Box sx={{ ...cardSx, p: { xs: 2, md: 2.5 } }}>
              <Stack direction="column" spacing={1.5}>
                <Typography variant="body1" fontWeight={"bold"}>
                  {t("searchByThingType")}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t("searchByThingTypeDescription")}
                </Typography>
                <Box
                  sx={{
                    bgcolor: "grey.50",
                    border: "1px solid",
                    borderColor: "rgba(0, 0, 0, 0.06)",
                    borderRadius: 1.5,
                    p: 1.5,
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    justifyContent="space-between"
                    width="100%"
                  >
                    <Typography variant="body2" fontWeight={"bold"}>
                      {t("thingType")}
                    </Typography>
                    {/* collapec expend button */}
                    <IconButton
                      onClick={() => {
                        setThingTypeFilterExpanded(!thingTypeFilterExpanded)
                      }}
                      size="small"
                    >
                      {thingTypeFilterExpanded ? (
                        <KeyboardArrowUp fontSize="small" />
                      ) : (
                        <KeyboardArrowDown fontSize="small" />
                      )}
                    </IconButton>
                  </Stack>
                  {thingTypeFilterExpanded && (
                    <FormGroup row sx={{ mt: 1 }}>
                      {ThingTypeMrns.map(({ value, label }) => (
                        <FormControlLabel
                          key={value}
                          control={
                            <Checkbox
                              checked={thingType === value}
                              onChange={(e) => {
                                setThingType(e.target.checked ? value : "")
                              }}
                              name={value}
                            />
                          }
                          label={
                            <Typography variant="body2">{t(label)}</Typography>
                          }
                          sx={{
                            width: 300,
                          }}
                        />
                      ))}
                    </FormGroup>
                  )}
                </Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography
                    variant="body2"
                    sx={{ color: "text.secondary", fontWeight: 500 }}
                  >{`${thingTypeEvents?.length} events`}</Typography>
                  {isThingTypeLoading ? (
                    <CircularProgress size={22} />
                  ) : (
                    <IconButton
                      onClick={() => {
                        qThingType.refetch()
                      }}
                      size="small"
                      sx={{
                        bgcolor: "rgba(35, 66, 133, 0.06)",
                        color: "primary.main",
                        "&:hover": {
                          bgcolor: "rgba(35, 66, 133, 0.12)",
                        },
                      }}
                    >
                      <Refresh fontSize="small" />
                    </IconButton>
                  )}
                </Stack>
              </Stack>
            </Box>
            <Box sx={{ ...cardSx, overflow: "hidden" }}>
              <MeventsList
                mevents={thingTypeEvents}
                onTraceIdClick={(traceId) => {
                  setTraceId(traceId)
                  setLocalTraceId(traceId)
                }}
              />
            </Box>
          </Stack>
        </Stack>
      </Box>
    </SaLayout>
  )
}

export default SaMeventsPage
